/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { RequestScope, ScopeType } from '../actions/api';
import { getFrameworks, getStandards } from '../constants/standards-frameworks';
import { Scope, ScopeGroups } from '../model/surveyData';
import {
  CombinedDataScopeAccess,
  DataScope,
  DataScopeAccess,
  DataShareMin,
  DataShareScopeView,
  DataShareStatus,
} from '../types/dataShare';
import { handleRouteError } from '../logger';
import { InitiativeCompany, RequestReviewMethod } from '../types/initiative';

export const MESSAGE = {
  NO_ACCESS: 'Data sharing is turned off. All requests from Portfolio Tracker organisations are automatically rejected.',
  NO_REQUEST: 'You have no data sharing requests.',
  SHARE_NOTE: 'Only information from surveys that are marked as \'Complete\' is shared. Data marked as \'Private\' is never shared.'
}

// Moving forward should only use combinedDataScopeAccess and no longer optional
export const getDataShareScopeConstraints = (
  dataShares: DataShareMin[],
  combinedDataScopeAccess: CombinedDataScopeAccess | undefined
): { access: DataScopeAccess, scopeConstraint: Set<string> } => {

  const scopeConstraint = new Set<string>();

  if (combinedDataScopeAccess) {
    const access = combinedDataScopeAccess.access;

    if (access === DataScopeAccess.Full) {
      return { scopeConstraint, access };
    }
    if (access === DataScopeAccess.None) {
      return { scopeConstraint, access };
    }

    if (access === DataScopeAccess.Partial) {
      if (combinedDataScopeAccess.scope) {
        return {
          scopeConstraint: new Set(Object.values(combinedDataScopeAccess.scope).flat()),
          access: access,
        };
      }
      handleRouteError(new Error('Missing required dataScope scope for partial access'), {
        combinedDataScopeAccess,
        dataShares,
      })
    }
  }

  // TODO [DATA-SHARE] Old way, for backward compatability, remove moving forward
  for (const dataShare of dataShares) {
    const scope = dataShare.dataScope?.survey?.scope;
    const dataScopeAccess = dataShare.dataScope?.survey?.access;

    if (dataScopeAccess === DataScopeAccess.None) {
      continue; // Ignore it
    }

    // Old missing scope still supported...
    if (!scope || dataScopeAccess === DataScopeAccess.Full) {
      // Unrestricted access, clear
      scopeConstraint.clear();
      return { access: DataScopeAccess.Full, scopeConstraint };
    }

    Object.values(scope)
      .flat()
      .forEach((code) => scopeConstraint.add(code));
  }

  return {
    scopeConstraint,
    access: scopeConstraint.size === 0 ? DataScopeAccess.None : DataScopeAccess.Partial,
  };
};

export const isViewShared = (
  company: Pick<InitiativeCompany, 'type' | 'requestedDataShares'>,
  view: DataShareScopeView
) => {
  const dataShares = company.requestedDataShares ?? [];
  return dataShares.some((dataShare) => dataShare.status === DataShareStatus.Accepted && (dataShare.dataScope?.survey?.views ?? []).includes(view));
};

export const getViewAccess = (company: Pick<InitiativeCompany, 'requestedDataShares'>) => {
  const acceptedDataShares = (company.requestedDataShares ?? []).filter((item) => item.status === DataShareStatus.Accepted);

  if (!acceptedDataShares.length) {
    return DataScopeAccess.None;
  }

  const mergedScopeViews = mergeScopeViews(acceptedDataShares);
  const numbOfScopeViews = Object.keys(DataShareScopeView).length;
  if (mergedScopeViews.length === numbOfScopeViews) {
    return DataScopeAccess.Full;
  }
  if (mergedScopeViews.length > 0) {
    return DataScopeAccess.Partial;
  }
  return DataScopeAccess.None;
};

export const isNoAccess = (company: Pick<InitiativeCompany, 'dataShare'>) => company.dataShare?.requestReview === RequestReviewMethod.NoAccess;

export const canAccessData = (dataScope: DataScope | undefined) => {
  return ([DataScopeAccess.Full, DataScopeAccess.Partial] as DataScopeAccess[]).includes(getScopeAccess(dataScope));
}

export const hasFullScopeAccess = (company: Pick<InitiativeCompany, 'requestedDataShares'>) => {
  const dataShares = company.requestedDataShares ?? [];
  return dataShares.some((dataShare) => dataShare.status === DataShareStatus.Accepted && getScopeAccess(dataShare.dataScope) === DataScopeAccess.Full);
};

export const getScopeAccess = (dataScope: DataScope | undefined) => {
  if (!dataScope) {
    return DataScopeAccess.None;
  }
  if (!dataScope.survey) {
    return DataScopeAccess.Full;
  }
  if (dataScope.survey.access) {
    return dataScope.survey.access;
  }
  if (!dataScope.survey.scope) {
    return DataScopeAccess.Full
  }
  return DataScopeAccess.Partial;
}

export const getScopeViews = (dataScope: DataScope | undefined) => dataScope?.survey?.views ?? [];
export const mergeScopeViews = (dataShare: DataShareMin[]): DataShareScopeView[] => {
  const mergedViews = new Set<DataShareScopeView>();

  dataShare.forEach(share => {
    const shareViews = getScopeViews(share.dataScope);
    shareViews.forEach(v => {
      mergedViews.add(v);
    })
  });

  return Array.from(mergedViews);
}

export const mergeScopes = (scope: Scope, requestScopes: RequestScope[]) => {
  const mergedScope = {
    [ScopeGroups.Sdg]: new Set(scope[ScopeGroups.Sdg]),
    [ScopeGroups.Materiality]: new Set(scope[ScopeGroups.Materiality]),
    [ScopeGroups.Standards]: new Set(scope[ScopeGroups.Standards]),
    [ScopeGroups.Frameworks]: new Set(scope[ScopeGroups.Frameworks]),
    [ScopeGroups.Custom]: new Set(scope[ScopeGroups.Custom]),
  }
  requestScopes.forEach(reqScope => {
    mergedScope[reqScope.scopeType as ScopeGroups].add(reqScope.code)
  })
  return mergedScope;
};

export const getScopeOptions = (dataScope: DataScope | undefined): RequestScope[] => {
  const { standards = [], frameworks = [], custom = [] } = dataScope?.survey?.scope ?? ({} as Scope);
  const getOptions = (packs: string[], scopeType: ScopeType) => packs.map((item) => ({ code: item, scopeType }));
  return [
    ...getOptions(standards, 'standards'),
    ...getOptions(frameworks, 'frameworks'),
    ...getOptions(custom, 'custom'),
  ];
};

export const getDataScopeSurvey = (dataScope: DataScope | undefined) => {
  return dataScope?.survey ?? {};
};

export const convertScope = (selectedScopes: RequestScope[]) => {
  return selectedScopes.reduce(
    (acc, cur) => {
      switch (cur.scopeType) {
        case 'standards':
          acc.standards.push(cur.code);
          break;
        case 'frameworks':
          acc.frameworks.push(cur.code);
          break;
        case 'custom':
          acc.custom.push(cur.code);
          break;
        case 'sdg':
          acc.sdg.push(cur.code);
          break;
        case 'materiality':
          acc.materiality.push(cur.code);
          break;
        default:
          break;
      }
      return acc;
    },
    { standards: [], frameworks: [], custom: [], sdg: [], materiality: [] } as Scope
  );
};

export const getDefaultShareSurveyScope = (): Scope => ({
  [ScopeGroups.Sdg]: [],
  [ScopeGroups.Standards]: getStandards().map((s) => s.code),
  [ScopeGroups.Frameworks]: getFrameworks().map((f) => f.code),
  [ScopeGroups.Custom]: [],
  [ScopeGroups.Materiality]: [],
});

const getAllViews = () => (Object.values(DataShareScopeView) as DataShareScopeView[]);

export const getDefaultRequesterDataScope = (): DataScope => ({
  survey: {
    scope: undefined,
    views: getAllViews(),
    access: DataScopeAccess.Full,
  },
});
